import clsx from 'clsx';
import moment from 'moment-timezone';

import { useEffect, useRef, useState } from 'react';
import { DateTimeEntry } from '@/datetime/DateTimeEntry.atom';
import type { Timezone } from '@/datetime/timezone.service';

export interface RangeField<T> {
  start: T;
  end: T;
}

type Range = RangeField<moment.Moment>;

/**
 * Ensures the range invariant (start ≤ end) is maintained.
 * Returns the adjusted range and which field was adjusted (if any).
 */
export const ensureValidRange = (
  range: Range,
  changed: 'start' | 'end',
): { range: Range; adjusted: 'start' | 'end' | null } => {
  if (changed === 'start' && range.start.isAfter(range.end)) {
    return { range: { ...range, end: moment(range.start) }, adjusted: 'end' };
  }
  if (changed === 'end' && range.end.isBefore(range.start)) {
    return { range: { ...range, start: moment(range.end) }, adjusted: 'start' };
  }
  return { range, adjusted: null };
};

type DateTimeRangeProps = {
  startTime: moment.Moment;
  endTime: moment.Moment;
  timezone: Timezone;
  hideTimezone: boolean;
  onStartTimeChange?: (value: moment.Moment) => void;
  onEndTimeChange?: (value: moment.Moment) => void;
  onChange?: (value: Range) => void;
  dateFormat?: string;
  trackCategory?: string;
  trackAction?: string;
  trackInformation?: RangeField<string>;
  disabled?: boolean;
  autoUpdateDate?: boolean;
  datePickerExtraClassNames?: string;
};

export const DateTimeRange = ({
  startTime,
  endTime,
  onStartTimeChange,
  onEndTimeChange,
  dateFormat,
  timezone,
  trackCategory,
  trackAction,
  trackInformation,
  hideTimezone,
  onChange,
  disabled,
  autoUpdateDate,
  datePickerExtraClassNames,
}: DateTimeRangeProps) => {
  // State for immediate UI display (e.g., showing adjusted values before parent confirms)
  const [range, setRange] = useState<Range>({ start: startTime, end: endTime });

  // Ref tracks pending changes synchronously for rapid sequential updates
  // (e.g., flatpickr's onChange + onDone firing in the same event loop).
  // State updates are async, so the ref ensures we always have the latest value.
  const pendingRange = useRef<Range>(range);

  // Sync state and ref when props change (parent confirmed our updates)
  useEffect(() => {
    const newRange = { start: startTime, end: endTime };
    setRange(newRange);
    pendingRange.current = newRange;
  }, [startTime.valueOf(), endTime.valueOf()]);

  const updateDate = (date: moment.Moment | undefined, field: 'start' | 'end') => {
    if (!date) return;

    const newRange = { ...pendingRange.current, [field]: date };
    const { range: validRange, adjusted } = ensureValidRange(newRange, field);

    // Update both ref (synchronous) and state (triggers re-render)
    pendingRange.current = validRange;
    setRange(validRange);

    if (field === 'start' || adjusted === 'start') onStartTimeChange?.(validRange.start);
    if (field === 'end' || adjusted === 'end') onEndTimeChange?.(validRange.end);
    onChange?.(validRange);
  };

  const updateStartDate = (date: moment.Moment | undefined) => updateDate(date, 'start');
  const updateEndDate = (date: moment.Moment | undefined) => updateDate(date, 'end');

  return (
    <div className="flexColumnContainer text-center tw:items-center tw:justify-center">
      <DateTimeEntry
        testId="rangeStart"
        extraClassName="specRange specRangeStart"
        datePickerExtraClassNames={clsx('sq-text-primary flexRowContainer', datePickerExtraClassNames)}
        date={range.start}
        updateDate={updateStartDate}
        dateFormat={dateFormat}
        timezone={timezone}
        hideTimezone={hideTimezone}
        trackCategory={trackCategory}
        trackAction={trackAction}
        trackInformation={trackInformation?.start}
        readOnly={disabled}
        autoUpdateDate={autoUpdateDate}
      />
      <span className="tw:pr-sq-5 tw:pl-sq-5">-</span>
      <DateTimeEntry
        testId="rangeEnd"
        extraClassName="specRange specRangeEnd"
        datePickerExtraClassNames={clsx('sq-text-primary flexRowContainer', datePickerExtraClassNames)}
        date={range.end}
        updateDate={updateEndDate}
        dateFormat={dateFormat}
        timezone={timezone}
        hideTimezone={hideTimezone}
        trackCategory={trackCategory}
        trackAction={trackAction}
        trackInformation={trackInformation?.end}
        readOnly={disabled}
        autoUpdateDate={autoUpdateDate}
      />
    </div>
  );
};
